import Component from '@glimmer/component';

import { action } from '@ember/object';
import LinkToAction from 'ember-route-state/components/link-to-action.gjs';
import { linkToAction } from 'ember-route-state/decorators/link-to-action.js';

import fnx from 'rise-ui/helpers/fnx.js';
import gte from 'rise-ui/helpers/gte.js';
import not from 'rise-ui/helpers/not.js';
import or0 from 'rise-ui/helpers/or.js';
import { or } from 'rise-ui/utils/object/computed.js';
import { clamp } from 'rise-ui/utils/number.js';
import { pageLinks, totalPages } from 'rise-ui/utils/pages.js';

/**
 * Link generator
 * @callback PageControlsComponent~link
 * @param {number} page Page number requested by user
 * @return {*|[]} Params for link-to to change page
 */
/**
 * Page change event
 * @callback PageControlsComponent~action
 * @param {number} page Page number requested by user
 */
/**
 * Displays pagination controls and information
 * @typedef PageControlsComponent
 * @type {PageControlsComponent & LinkToActionDecoratedComponent}
 * @property {{}} args Component args
 * @property {number} args.page Current page
 * @property {number} args.pages Total pages
 * @property {{first:*,previous:*,next:*,last:*,}|PageControlsComponent~link} args.links
 *   Hash of linkTo params or function. If it is function, it is passed desired
 *   page number and should return linkTo params.
 * @property {PageControlsComponent~action} args.action Called with page on change
 * @property {{}} args.config Aggregate configuration hash
 */
@linkToAction
export default class PageControlsComponent extends Component {
  @or('args.{disabled,config.disabled}') isDisabled;
  @or('args.{pending,config.pending}') isPending;
  @or('args.{key,config.key}', or.value('page')) key;
  @or('args.{items,config.items}') itemsInput;
  @or('args.{itemsPerPage,config.itemsPerPage}') itemsPerPageInput;

  @or('args.{page,config.page}') pageInput;
  // Clamped current page number
  get currentPage() {
    return Math.max(1, +(this.pageInput || 1));
  }

  // Attributes @pages, @config.pages
  @or('args.{pages,config.pages}') pagesInput;
  // Clamped page count
  get totalPages() {
    const { itemsInput, itemsPerPageInput, pagesInput } = this;
    const total = pagesInput
      ?? totalPages(itemsInput, itemsPerPageInput);
    return Math.max(1, +(total || 1));
  }

  // Clamped previous page number
  get previousPage() {
    return clamp(1, this.currentPage - 1, this.lastPage);
  }
  // Clamped next page number
  get nextPage() {
    return clamp(1, this.currentPage + 1, this.lastPage);
  }
  // Clamped last page number
  get lastPage() {
    return Math.max(this.currentPage, this.totalPages);
  }

  // Whether to display page number and total
  get hasPageInfo() {
    const pages = this.totalPages;
    return !!(pages && +pages > 0);
  }
  // Whether to disable first/previous links
  get isFirstPage() {
    return +this.currentPage === 1;
  }
  // Whether to disable next/last links
  get isLastPage() {
    return +this.currentPage === this.totalPages;
  }

  // Attributes @link, @links, @config.link, @config.links
  @or('args.{link,links,config.link,config.links}') linksInput;
  // Params for link-to for changing page
  get linkTo() {
    const {
      key,
      previousPage, currentPage, nextPage, lastPage, totalPages,
      linksInput: links,
    } = this; // Consume
    let result = { first: [], previous: [], next: [], last: [] };
    if (typeof links === 'function') {
      result = {
        first:    links(1),
        previous: links(previousPage),
        next:     links(nextPage),
        last:     links(lastPage),
      };
    } else if (links) {
      result = { ...result, ...links };
    } else {
      result = {
        ...result,
        ...pageLinks(key, currentPage || 1, totalPages),
      };
    }
    return result;
  }

  @or('args.{action,config.action,onPageChange}') pageChangeHandler;

  // On page change: callback with page
  @action action(page, ...args) {
    return this.callLinkToActionByName('pageChangeHandler', page, ...args);
  }

  <template>
    <div class="c-page-controls">
      <span class="c-page-controls__back">
        <span class="c-page-controls__first">
          <LinkToAction
            @disabled={{or0 this.isDisabled this.isFirstPage}}
            @linkTo={{this.linkTo.first}}
            @action={{fnx this.action 1}}
          >First</LinkToAction>
        </span>
        <span class="c-page-controls__previous">
          <LinkToAction
            @disabled={{or0 this.isDisabled this.isFirstPage}}
            @linkTo={{this.linkTo.previous}}
            @action={{fnx this.action this.previousPage}}
          >Previous</LinkToAction>
        </span>
      </span>
      <span class="c-page-controls__label">
        {{~#if (has-block)}}
          {{yield}}
        {{else if this.hasPageInfo}}
          {{~#if this.isPending~}}
            Loading
          {{~/if}}
          Page {{this.currentPage}}
          {{#if (gte this.totalPages this.currentPage)~}}
            of {{this.totalPages}}
          {{~/if~}}
        {{~/if~}}
      </span>
      <span class="c-page-controls__forward">
        <span class="c-page-controls__next">
          <LinkToAction
            @disabled={{or0 this.isDisabled this.isLastPage (not this.hasPageInfo)}}
            @linkTo={{this.linkTo.next}}
            @action={{fnx this.action this.nextPage}}
          >Next</LinkToAction>
        </span>
        <span class="c-page-controls__last">
          <LinkToAction
            @disabled={{or0 this.isDisabled this.isLastPage (not this.hasPageInfo)}}
            @linkTo={{this.linkTo.last}}
            @action={{fnx this.action this.lastPage}}
          >Last</LinkToAction>
        </span>
      </span>
    </div>
  </template>
}
